/**
 * AB#21246 — the name of the page you are on.
 *
 * The header's heading was the literal string "Dashboard", so every screen in
 * the admin — Users, Factories, Price List, Translations — announced itself as
 * the dashboard. The browser tab was already correct via `setPageTitle`; only
 * the thing a person actually looks at was wrong.
 *
 * The sidebar already holds the right words. Those names are what the user
 * clicked to get here, so matching the route back to the navigation tree gives
 * a title that agrees with the menu instead of inventing a second vocabulary.
 */

export interface NavItem {
  name: string;
  to: string | null;
  /** Lucide icon name, as the sidebar resolves it. */
  icon_id?: string | number | null;
  children?: NavItem[];
}

/** Routes with no menu entry of their own, or whose menu name reads poorly. */
const OVERRIDES: Record<string, string> = {
  '/': 'Dashboard',
  '/dashboard': 'Dashboard',
  '/not-authorized': 'Not authorised',
  // AB#22166 — Finance section pages are not in the backend menu.
  '/finance/commission': 'Commission report',
};

/** Routes with parameters (no fixed path to override), by route name. */
const NAMED: Record<string, string> = {
  'edit-tds': 'Edit TDS',
};

/**
 * Flatten the navigation tree, deepest-first.
 *
 * A parent may carry a `to` that prefixes its children ("/settings" over
 * "/settings/email"), so the longest match has to win or every settings page
 * would be titled "Settings".
 */
const flatten = (items: NavItem[] | null | undefined): NavItem[] => {
  const out: NavItem[] = [];
  const walk = (list: NavItem[] | null | undefined, depth: number) => {
    if (!Array.isArray(list) || depth > 6) return;
    for (const item of list) {
      if (item && typeof item.name === 'string') out.push(item);
      walk(item?.children, depth + 1);
    }
  };
  walk(items, 0);
  return out;
};

/** "design-categories" → "Design Categories". */
export const humanise = (slug: string | null | undefined): string =>
  (slug ?? '')
    .split(/[-_/]/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

/**
 * Resolve the heading for a route.
 *
 * Preference order, most specific first:
 *   1. an explicit override
 *   2. the longest matching navigation entry — the words in the sidebar
 *   3. the route's own name, humanised
 *   4. "Dashboard", so the header is never blank
 */
export const pageTitleFor = (
  path: string | null | undefined,
  routeName: string | null | undefined,
  navigation: NavItem[] | null | undefined
): string => {
  const clean = (path ?? '').split('?')[0].replace(/\/+$/, '') || '/';

  if (OVERRIDES[clean]) return OVERRIDES[clean];

  const match = matchNavItem(path, navigation);
  if (match && match.name.trim()) return match.name.trim();

  const fromRoute = (routeName ? NAMED[routeName] : undefined) ?? humanise(routeName);
  if (fromRoute) return fromRoute;

  return 'Dashboard';
};

/**
 * The navigation entry a route belongs to, or null.
 *
 * Shared by the title and the icon so the two can never disagree about which
 * menu entry the current page is — the header showing one section's name beside
 * another section's icon would be worse than either being wrong alone.
 */
export const matchNavItem = (
  path: string | null | undefined,
  navigation: NavItem[] | null | undefined
): NavItem | null => {
  const clean = (path ?? '').split('?')[0].replace(/\/+$/, '') || '/';

  const matches = flatten(navigation)
    .filter((item) => {
      const to = (item.to ?? '').split('?')[0].replace(/\/+$/, '');
      // An entry with no path, or one pointing at the root, matches everything
      // and so must match nothing. Entries pointing at another application
      // cannot name a route here either.
      if (!to || to === '/' || /^https?:/i.test(to)) return false;
      return clean === to || clean.startsWith(`${to}/`);
    })
    // Longest `to` wins, so a child beats the parent that prefixes it.
    .sort((a, b) => (b.to ?? '').length - (a.to ?? '').length);

  return matches[0] ?? null;
};

/**
 * The lucide icon name for a route, resolved the same way the sidebar resolves
 * its own — so the glyph beside the page title is the glyph you clicked.
 *
 * Child entries in the menu carry no icon of their own (the sidebar draws
 * none for them), so a child page — Roles under ACL Management, Factory
 * Settings under Factory — takes the icon of the nearest ancestor that has
 * one. That is the icon the user opened to reach the page. Only a page the
 * menu does not know at all gets the neutral fallback; it must never be the
 * dashboard's icon, which is exactly the bug this exists to fix: every
 * screen announcing itself as the dashboard.
 */
export const pageIconFor = (
  path: string | null | undefined,
  navigation: NavItem[] | null | undefined,
  fallback = 'LayoutGrid'
): string => {
  const match = matchNavItem(path, navigation);
  if (!match) return fallback;
  for (const item of [match, ...ancestorsOf(match, navigation)]) {
    const id = item.icon_id;
    if (typeof id === 'string' && id.trim()) return id;
  }
  return fallback;
};

/** The chain of parents above an item, nearest first. */
const ancestorsOf = (target: NavItem, navigation: NavItem[] | null | undefined): NavItem[] => {
  const walk = (
    list: NavItem[] | null | undefined,
    trail: NavItem[],
    depth: number
  ): NavItem[] | null => {
    if (!Array.isArray(list) || depth > 6) return null;
    for (const item of list) {
      if (item === target) return trail;
      const found = walk(item?.children, [item, ...trail], depth + 1);
      if (found) return found;
    }
    return null;
  };
  return walk(navigation, [], 0) ?? [];
};
